import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Select, Input, Button, Space, message, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';

interface SchoolOption {
    name: string;
    value: string;
}

interface CustomSchoolSelectProps {
    value?: string;
    onChange?: (value: string) => void;
    options?: SchoolOption[];
    placeholder?: string;
    className?: string;
    onCustomSchoolSubmit?: (schoolName: string) => void;
}

export const CustomSchoolSelect: React.FC<CustomSchoolSelectProps> = ({
    value,
    onChange,
    options = [],
    placeholder = "请选择毕业学校",
    className,
    onCustomSchoolSubmit
}) => {
    const [customSchoolVisible, setCustomSchoolVisible] = useState(false);
    const [customSchoolName, setCustomSchoolName] = useState('');
    const [schoolOptions, setSchoolOptions] = useState<SchoolOption[]>(options);
    const [showOptions, setShowOptions] = useState<SchoolOption[]>([]);
    const inputRef = useRef<any>(null);

    // 初始化选项，添加自定义选项
    useEffect(() => {
        const enhancedOptions = [...options];
        
        // 添加自定义选项到列表最后
        if (!enhancedOptions.some(opt => opt.value === 'custom')) {
            enhancedOptions.push({
                name: '其他学校',
                value: 'custom'
            });
        }
        
        setSchoolOptions(enhancedOptions);
        setShowOptions(enhancedOptions);
    }, [options]);

    // 处理选择变化
    const handleSelectChange = (selectedValue: string) => {
        if (selectedValue === 'custom') {
            // 选中其他学校，显示输入框
            setCustomSchoolVisible(true);
            setCustomSchoolName('');
            onChange?.('');
            
            // 聚焦到输入框
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        } else {
            // 选中普通学校，隐藏输入框
            setCustomSchoolVisible(false);
            onChange?.(selectedValue);
            setUpdateValue(selectedValue);
            setShowOptions(schoolOptions);
        }
        
    };

    // 处理其他学校提交
    const handleCustomSchoolSubmit = () => {
        if (!customSchoolName.trim()) {
            message.warning('请输入学校名称');
            return;
        }
        let customName = `-1~${customSchoolName.trim()}`
        // 提交其他学校
        if (onCustomSchoolSubmit) {
            onCustomSchoolSubmit(customName);
        }

        // 更新选项列表，添加新的其他学校
        const newSchoolOption = {
            name: customSchoolName.trim(),
            value: customName
        };

        // 检查是否已存在相同的学校
        if (!schoolOptions.some(opt => opt.value === newSchoolOption.value)) {
            const updatedOptions = [
                ...options.filter(opt => opt.value !== 'custom'),
                newSchoolOption,
                { name: '其他学校', value: 'custom' }
            ];
            setShowOptions(updatedOptions);
        }

        // 设置选中值，前面需要加上-1~用来区分其他学校
        onChange?.(customName);
        setUpdateValue(customSchoolName);
        setCustomSchoolVisible(false);
        setCustomSchoolName('');
        
        message.success('其他学校已添加');
    };

    // 处理输入框变化
    const handleCustomSchoolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomSchoolName(e.target.value);
    };

    // 处理输入框回车事件
    const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleCustomSchoolSubmit();
        }
    };

    // 取消其他学校输入
    const handleCancelCustom = () => {
        setCustomSchoolVisible(false);
        setCustomSchoolName('');
        onChange?.('');
    };
    const [updateValue, setUpdateValue] = useState("");
    const [fetching, setFetching] = useState(true);

    const searchOptions = useCallback((value) => {
        let arr = [];
        console.log(schoolOptions, value, 'search')
        if(value){
            arr = schoolOptions.filter(item=>item.name && item.name.indexOf(value)>-1);
        } else {
            arr = [...schoolOptions];
        }
        
        return arr;
    }, [schoolOptions]);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value) => {
            setShowOptions([]);
            setFetching(true);
            let newOptions = searchOptions(value);
            setShowOptions(newOptions);
            setFetching(false);
        };
        return debounce(loadOptions, 300);
    }, [searchOptions]);
    useEffect(()=>{
        let schoolName = value && value.indexOf("~")>-1? value.substr(value.indexOf("~")+1): value;
        setUpdateValue(schoolName);
    }, []);
    return (
        <div className={`custom-school-select ${className || ''}`}>
            {/* 主选择框 */}
            <Select
                value={customSchoolVisible ? 'custom' : updateValue}
                onChange={handleSelectChange}
                placeholder={placeholder}
                showSearch={true}
                className="CommonSelect teminput"
                getPopupContainer={(node) => node.parentNode}
                // labelInValue
                filterOption={false}
                onSearch={debounceFetcher}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                // dropdownStyle={{ display: customSchoolVisible ? 'none' : 'block' }}
            >
                {/* <input className='option-search' type="text" placeholder='输入学校名称搜索...' onChange={(e)=>setKeyword(e.target.value)} /> */}
                {showOptions.map((option, index) => (
                    <Select.Option 
                        key={option.value} 
                        value={option.value}
                        label={option.name}
                    >
                        {option.name}
                    </Select.Option>
                ))}
            </Select>

            {/* 其他学校输入框 */}
            {customSchoolVisible && (
                <div style={{ marginTop: 8, padding: 8, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {/* <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            请输入其他学校名称：
                        </div> */}
                        <Input
                            ref={inputRef}
                            value={customSchoolName}
                            onChange={handleCustomSchoolChange}
                            onKeyPress={handleInputKeyPress}
                            placeholder="请输入学校名称"
                            maxLength={50}
                        />
                        <Space>
                            <Button 
                                type="primary" 
                                size="small" 
                                onClick={handleCustomSchoolSubmit}
                                icon={<PlusOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
                            >
                                添加
                            </Button>
                            <Button 
                                size="small" 
                                onClick={handleCancelCustom}
                            >
                                取消
                            </Button>
                        </Space>
                    </Space>
                </div>
            )}
        </div>
    );
};